import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, THistoryFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import CEditor from 'src/components/editor/CEditor';
import FormProvider, { RHFSwitch, RHFTextField } from 'src/components/hook-form';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import { FormItem, FormRow } from 'src/layouts/form/FormLayout';
import { IHistory, useStores } from 'src/models';
import { historyApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getHistoryFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface IHistoryFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IHistory;
}

export const HistoryFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IHistoryFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;

    const methods = useForm<THistoryFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: getHistoryFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: THistoryFormData) => {
      const formData = {
        historyDate: data.historyDate ?? '',
        titleKo: data.titleKo,
        titleEn: data.titleEn,
        descript: data.descript,
        useYn: data.useYn,
      };

      const responseText = isCreate ? '등록' : '수정';
      const result =
        !isCreate && selectedData
          ? historyApi.put(selectedData.historySid, formData)
          : historyApi.post(formData);

      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (selectedData?.historySid) {
          historyApi
            .remove(selectedData.historySid)
            .then(() =>
              handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
            );
        }
      });
      alertStore.setProps({
        open: true,
        title: '연혁 삭제',
        content: '해당 연혁을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };
    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
            <FormItem>
              <RHFDatePicker label={t('history.form.historyDate')} name="historyDate" />
            </FormItem>
            <FormItem>
              <RHFSwitch label={t('history.form.useYn')} name="useYn" />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <RHFTextField label={t('history.form.titleKo')} name="titleKo" disabled={readonly} />
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField label={t('history.form.titleEn')} name="titleEn" disabled={readonly} />
            </FormItem>
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <CEditor name={'descript'} placeholder={t('history.form.descript')} />
            </FormItem>
          </FormRow>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
